import { useEffect, useState } from "react";
import { Check, Lock, ShieldCheck, CreditCard } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { Reveal, Chapter } from "./Shared";
import { openRazorpayCheckout } from "../../lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_CREDITS = { trial: 1, starter: 150, growth: 250, empire: 400 };

const planKeyFor = (name) =>
  name === "Launch Trial"
    ? "trial"
    : name === "Starter Scale"
      ? "starter"
      : name === "Growth Pro"
        ? "growth"
        : name === "Empire Velocity"
          ? "empire"
          : "custom";

const PLANS = [
  {
    test: "pricing-card-trial",
    ctaTest: "pricing-trial-checkout-button",
    name: "Launch Trial",
    badge: "Risk-Free Test",
    price: "$1",
    cadence: "one-time",
    designs: "1 High-Res Custom Design",
    unit: "One-time · no subscription",
    desc: "Judge our human quality risk-free. Send a tough brief tonight, get files back by morning.",
    features: [
      "24hr Delivery",
      "Full Commercial Use",
      "Vector Path Hygiene",
    ],
    highlight: false,
    isTrial: true,
  },
  {
    test: "pricing-card-starter",
    ctaTest: "pricing-starter-checkout-button",
    name: "Starter Scale",
    badge: "POD Testbed",
    price: "$150",
    cadence: "/month",
    designs: "150 designs / mo",
    unit: "$1.00 per design",
    desc: "Ideal for burgeoning shops seeking continuous daily catalog refreshes.",
    features: [
      "150 custom vector designs / mo",
      "Guaranteed overnight 24h delivery",
      "Full commercial license included",
      "Organized cloud asset vault",
      "Printify & Printful specs matched",
      "Unlimited revisions on briefs",
    ],
    highlight: false,
  },
  {
    test: "pricing-card-growth",
    ctaTest: "pricing-growth-checkout-button",
    name: "Growth Pro",
    badge: "Most Popular",
    price: "$225",
    cadence: "/month",
    designs: "250 designs / mo",
    unit: "$0.90 per design",
    desc: "The sweet spot for established sellers dominating 2 to 3 high-volume seasonal niches.",
    features: [
      "250 custom vector designs / mo",
      "Guaranteed overnight 24h delivery",
      "Priority senior illustrator assigned",
      "Full commercial resale license",
      "Direct Slack / dashboard channel",
      "Source AI + high-resolution PNG",
      "Font licensing verification",
    ],
    highlight: true,
  },
  {
    test: "pricing-card-empire",
    ctaTest: "pricing-empire-checkout-button",
    name: "Empire Velocity",
    badge: "Multi-Shop Scale",
    price: "$300",
    cadence: "/month",
    designs: "400 designs / mo",
    unit: "$0.75 per design",
    desc: "Designed for digital download powerhouses running multi-store Etsy & Shopify ecosystems.",
    features: [
      "400 custom vector designs / mo",
      "Guaranteed overnight 24h delivery",
      "Dedicated 2-designer pod",
      "Full commercial license forever",
      "Batch CSV metadata export helper",
      "Custom mockup PSD preparation",
      "VIP priority queue",
    ],
    highlight: false,
  },
];

export default function Pricing() {
  const [selected, setSelected] = useState(null);
  const [flash, setFlash] = useState(false);
  const [volume, setVolume] = useState(500);
  const [email, setEmail] = useState("");
  const [buying, setBuying] = useState(false);

  const rateFor = (d) => {
    if (d >= 1000) return 0.5;
    if (d >= 500) return 0.65;
    if (d >= 400) return 0.75;
    if (d >= 250) return 0.9;
    return 1.0;
  };
  const vRate = rateFor(volume);
  const vMonthly = Math.round(volume * vRate);
  const vPct = Math.round(((volume - 50) / 950) * 100);

  useEffect(() => {
    const onHighlight = () => {
      setFlash(true);
      window.setTimeout(() => setFlash(false), 2800);
    };
    window.addEventListener("grapho:highlight-trial", onHighlight);
    return () => window.removeEventListener("grapho:highlight-trial", onHighlight);
  }, []);

  return (
    <section id="pricing" data-testid="pricing" className="relative">
      <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        <Reveal className="text-center">
          <Chapter num="04" label="Unit Economics" className="justify-center" />
          <h2 className="mx-auto mt-6 max-w-3xl text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Outsource your design department for less than a part-time hire
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-400">
            All plans include guaranteed 24-hour delivery, 100% commercial license and dedicated human
            craft. No surprise add-on fees.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <p
            data-testid="pricing-explainer"
            className="mx-auto mt-10 max-w-3xl text-center text-sm leading-relaxed text-slate-400 sm:text-base"
          >
            How do we deliver this at scale? Our production team operates across international timezones
            overnight — skilled vector artists and typographers working while you sleep. That timezone
            advantage is what makes $1 per design possible without cutting corners on quality.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="exclusivity-guarantee"
            className="mx-auto mt-6 flex max-w-3xl items-start gap-3 rounded-2xl border border-amber-400/40 bg-amber-400/5 px-5 py-4"
          >
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
            <p className="text-sm leading-relaxed text-amber-200/90">
              <span className="font-bold text-amber-300">Exclusivity Guarantee — </span>
              Every design brief is yours alone. We never deliver the same concept to two sellers. Your
              store stays unique.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {PLANS.map((plan, i) => (
            <Reveal key={plan.test} delay={i * 0.08} className="h-full">
              <div
                data-testid={plan.test}
                className={`card-lift relative flex h-full flex-col rounded-3xl border bg-[#0C1222]/80 p-6 ${
                  plan.highlight
                    ? "border-sky-400/40 shadow-[0_0_50px_rgba(56,189,248,0.15)]"
                    : "border-white/10"
                } ${plan.isTrial && flash ? "ring-2 ring-amber-400/80 shadow-[0_0_45px_rgba(245,158,11,0.35)]" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-lg font-bold tracking-tight text-white">{plan.name}</h3>
                  <span
                    className={`rounded-full px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.18em] ${
                      plan.highlight
                        ? "bg-amber-400 text-[#1A1204]"
                        : "border border-white/10 bg-white/5 text-slate-400"
                    }`}
                  >
                    {plan.badge}
                  </span>
                </div>

                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="text-4xl font-extrabold tracking-tight text-white">{plan.price}</span>
                  <span className="text-sm text-slate-500">{plan.cadence}</span>
                </div>
                <p className="mt-2 text-sm font-semibold text-sky-300">{plan.designs}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-amber-300/80">
                  {plan.unit}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">{plan.desc}</p>

                <ul className="mt-5 flex-1 space-y-2.5 border-t border-white/5 pt-5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[13px] leading-snug text-slate-300">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={plan.ctaTest}
                  onClick={() => setSelected(plan)}
                  className={`mt-6 inline-flex w-full items-center justify-center rounded-full py-3 text-sm font-bold transition-all duration-300 ${
                    plan.highlight
                      ? "bg-sky-400 text-[#06202E] hover:bg-sky-300 hover:shadow-[0_0_30px_rgba(56,189,248,0.45)]"
                      : plan.isTrial
                        ? "bg-amber-400 text-[#1A1204] hover:bg-amber-300 hover:shadow-[0_0_30px_rgba(245,158,11,0.45)]"
                        : "border border-white/15 bg-white/5 text-white hover:border-sky-400/50 hover:bg-white/10"
                  }`}
                >
                  {plan.isTrial ? "Claim $1 Launch Trial" : `Select ${plan.name}`}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.08}>
          <div
            data-testid="pricing-mini-faq"
            className="mx-auto mt-8 max-w-4xl space-y-2.5 rounded-2xl border border-white/10 bg-[#070B14]/60 p-6"
          >
            {[
              ["What happens to unused designs?", "Unused credits stay in your pool all month — submit briefs any time, with no daily caps."],
              ["Can I pause my plan?", "Yes — cancel anytime from your dashboard and restart whenever you're ready."],
              ["How many revisions do I get?", "Every brief includes free revisions until the design is production-ready."],
              ["What is the delivery time?", "Overnight — production-ready PNG files land in your dashboard before 8 AM EST."],
              ["Can I cancel anytime?", "Yes, one click from your dashboard — your plan stays active until the end of the billing period."],
            ].map(([q, a], i) => (
              <div key={q} data-testid={`pricing-mini-faq-${i}`} className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3">
                <p className="shrink-0 text-[13px] font-bold text-white sm:w-64">{q}</p>
                <p className="text-[13px] leading-snug text-slate-400">{a}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <div
            data-testid="volume-slider-section"
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0C1222]/80 p-7 sm:p-10"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 12% 0%, rgba(56,189,248,0.12), transparent 55%), radial-gradient(circle at 92% 100%, rgba(245,158,11,0.09), transparent 50%)",
              }}
            />
            <div className="relative grid gap-10 lg:grid-cols-[1fr_1.15fr] lg:items-center">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">
                  Custom Volume // Past The Plan Grid
                </p>
                <h3 className="mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  Scaling beyond 400 designs a month?
                </h3>
                <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400">
                  The more you scale, the lower your design footprint drops.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {[
                    ["$1.00", "150+"],
                    ["$0.90", "250+"],
                    ["$0.75", "400+"],
                    ["$0.65", "500+"],
                    ["$0.50", "1,000+"],
                  ].map(([rate, vol]) => (
                    <span
                      key={vol}
                      className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400"
                    >
                      {vol} designs · <span className="text-amber-300">{rate}</span>
                    </span>
                  ))}
                </div>
              </div>

              <div className="glass rounded-2xl p-6 sm:p-7">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p
                      data-testid="volume-output-designs"
                      className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400"
                    >
                      {volume.toLocaleString()} designs / mo
                    </p>
                    <p
                      data-testid="volume-output-price"
                      className="mt-2 text-5xl font-extrabold tracking-tight text-white"
                    >
                      ${vMonthly}
                      <span className="text-lg font-normal text-slate-500">/month</span>
                    </p>
                  </div>
                  <span
                    data-testid="volume-output-per-design"
                    className="rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 font-mono text-xs text-amber-300"
                  >
                    ~${vRate.toFixed(2)} per design
                  </span>
                </div>

                <input
                  data-testid="volume-slider"
                  type="range"
                  min="50"
                  max="1000"
                  step="10"
                  value={volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  className="volume-slider mt-8 w-full"
                  aria-label="Custom volume slider"
                  style={{
                    background: `linear-gradient(to right, #38BDF8 0%, #F59E0B ${vPct}%, rgba(255,255,255,0.1) ${vPct}%, rgba(255,255,255,0.1) 100%)`,
                  }}
                />
                <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-wider text-slate-600">
                  <span>50</span>
                  <span>1,000</span>
                </div>

                <button
                  data-testid="volume-cta-button"
                  onClick={() =>
                    setSelected({
                      name: "Custom Volume",
                      price: `$${vMonthly}`,
                      cadence: "/month",
                      designs: `${volume.toLocaleString()} designs / mo`,
                      credits: volume,
                      desc: "Custom volume tier — the more you scale, the lower your design footprint drops.",
                    })
                  }
                  className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-amber-400 py-3.5 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_30px_rgba(245,158,11,0.45)]"
                >
                  Lock This Volume Rate — ${vMonthly}/mo
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-10 flex justify-center">
          <p
            data-testid="pricing-checkout-notice"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-400"
          >
            <Lock className="h-3.5 w-3.5 text-emerald-400" />
            Secure 1-click checkout powered by our verified global merchant gateway — cancel or pause anytime.
          </p>
        </Reveal>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          data-testid="checkout-modal"
          className="rounded-3xl border-white/10 bg-[#0C1222] p-7 text-slate-100 sm:max-w-md"
        >
          {selected && (
            <>
              <div className="flex items-center gap-2">
                <Lock className="h-3.5 w-3.5 text-emerald-400" />
                <DialogTitle className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                  Secure Hosted Checkout
                </DialogTitle>
              </div>

              <div className="mt-6 flex items-end justify-between rounded-2xl border border-white/10 bg-white/5 p-5">
                <div>
                  <p className="text-lg font-bold text-white">{selected.name}</p>
                  <p className="text-xs text-slate-400">{selected.designs}</p>
                </div>
                <p className="text-3xl font-extrabold text-white">
                  {selected.price}
                  <span className="text-sm font-normal text-slate-500">{selected.cadence}</span>
                </p>
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between text-slate-400">
                  <span>Plan</span>
                  <span className="text-slate-200">{selected.price}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Commercial license</span>
                  <span className="text-emerald-300">Included</span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-2 font-semibold text-white">
                  <span>Total</span>
                  <span>{selected.price}</span>
                </div>
              </div>

              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                <ShieldCheck className="h-3.5 w-3.5" />
                Full commercial license — sell unlimited units, forever
              </div>

              <div className="mt-5">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Email for receipt & activation
                </label>
                <input
                  data-testid="checkout-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seller@yourshop.com"
                  className="w-full rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:border-sky-400/60 focus:outline-none focus:ring-2 focus:ring-sky-400/20"
                />
              </div>

              <button
                data-testid="checkout-complete-button"
                onClick={async () => {
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
                    toast.error("Enter a valid email to continue to checkout.");
                    return;
                  }
                  setBuying(true);
                  try {
                    const planKey = planKeyFor(selected.name);
                    const credits = selected.credits || PLAN_CREDITS[planKey];
                    const { data: order } = await axios.post(`${API}/billing/checkout`, {
                      email: email.trim(),
                      plan_key: planKey,
                      credit_quantity: credits,
                    });
                    toast.success("Opening secure checkout...");
                    setBuying(false);
                    // Close our dialog BEFORE the Razorpay iframe opens: the radix
                    // focus trap otherwise steals focus from the payment modal's
                    // phone/card inputs on mobile and breaks entry.
                    setSelected(null);
                    await openRazorpayCheckout({
                      order,
                      email: email.trim(),
                      description: selected.name,
                      onSuccess: async (res) => {
                        try {
                          const { data: v } = await axios.post(`${API}/billing/verify`, {
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature,
                          });
                          if (!v?.ok) throw new Error("verify failed");
                          const em = v.email ? `&email=${encodeURIComponent(v.email)}` : "";
                          window.location.assign(
                            `${window.location.origin}/welcome?plan=${v.plan_key}&credits=${v.credits}&activated=1${em}`
                          );
                        } catch (e) {
                          toast.error("Payment received but activation failed — contact support and we'll sort it immediately.");
                        }
                      },
                      onDismiss: () => setBuying(false),
                    });
                  } catch (err) {
                    const detail = err.response?.data?.detail;
                    toast.error(typeof detail === "string" ? detail : "Checkout failed — please try again.");
                    setBuying(false);
                  }
                }}
                disabled={buying}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 py-3.5 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_30px_rgba(245,158,11,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Lock className="h-4 w-4" />
                {buying ? "Opening secure checkout..." : `Complete Purchase — ${selected.price}`}
              </button>
              <p className="mt-3 inline-flex w-full items-center justify-center gap-1.5 text-center text-xs text-slate-600">
                <CreditCard className="h-3 w-3 shrink-0" />
                Card details are entered on our payment provider's secure hosted checkout — credits activate automatically after payment.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
